import { v4 as uuidv4 } from 'uuid';

/** Shared with Mixpanel. */
export const ANONYMOUS_ID_COOKIE = 'pilar_anon_id';

// Without an explicit `maxAge` the cookie becomes a session cookie and is
// dropped when the browser closes, breaking attribution between visits.
const ANONYMOUS_ID_MAX_AGE_SEC = 60 * 60 * 24 * 365 * 2;

const SESSION_STORAGE_KEY = 'visitor_session';
const SESSION_LOCAL_STORAGE_KEY = 'visitor_session_bridge';

/** Matches GA/Mixpanel session semantics. */
export const SESSION_INACTIVITY_TTL_MS = 35 * 60 * 1000;

interface SessionBridge {
	data: string;
	lastActivity: number;
}

function parseJSON<T>(raw: string | null): T | null {
	if (!raw) return null;
	try {
		return JSON.parse(raw) as T;
	} catch {
		return null;
	}
}

function generateUUID(): string {
	return uuidv4();
}

export function getOrCreateAnonymousId(): string {
	const isDevelopment = process.env.NODE_ENV === 'development';

	const cookie = useCookie<string | null>(ANONYMOUS_ID_COOKIE, {
		maxAge: ANONYMOUS_ID_MAX_AGE_SEC,
		sameSite: 'lax',
		secure: !isDevelopment,
		default: () => null
	});

	if (!cookie.value) {
		cookie.value = generateUUID();
	}

	return cookie.value as string;
}

function readBridge(): string | null {
	try {
		const bridge = parseJSON<SessionBridge>(
			localStorage.getItem(SESSION_LOCAL_STORAGE_KEY)
		);
		if (!bridge?.data) return null;

		const elapsed = Date.now() - bridge.lastActivity;
		if (elapsed > SESSION_INACTIVITY_TTL_MS) {
			localStorage.removeItem(SESSION_LOCAL_STORAGE_KEY);
			return null;
		}

		return bridge.data;
	} catch {
		return null;
	}
}

function writeBridge(sessionId: string): void {
	try {
		const bridge: SessionBridge = {
			data: sessionId,
			lastActivity: Date.now()
		};
		localStorage.setItem(SESSION_LOCAL_STORAGE_KEY, JSON.stringify(bridge));
	} catch {
		/* quota exceeded or private mode — non-critical */
	}
}

function setSessionInStorage(sessionId: string): void {
	if (typeof window === 'undefined') return;

	try {
		sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
		writeBridge(sessionId);
	} catch (error) {
		console.error('Failed to save session id:', error);
	}
}

// The localStorage bridge is the source of truth for the active session;
// sessionStorage is only a per-tab cache. When the bridge is expired or
// absent we mint a new id even if the current tab still has a stale value.
export function getOrCreateSessionId(): string {
	if (typeof window === 'undefined') return '';

	const bridgeData = readBridge();
	if (bridgeData) {
		const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
		if (stored !== bridgeData) {
			sessionStorage.setItem(SESSION_STORAGE_KEY, bridgeData);
		}
		return bridgeData;
	}

	const newId = generateUUID();
	setSessionInStorage(newId);
	return newId;
}

export function syncSessionToSessionStorage(): void {
	if (typeof window === 'undefined') return;
	getOrCreateSessionId();
}

// Heartbeat for the inactivity timer. Does NOT recreate an expired bridge —
// `getOrCreateSessionId` is responsible for minting a new session id.
export function refreshSessionActivity(): void {
	if (typeof window === 'undefined') return;

	try {
		const bridge = parseJSON<SessionBridge>(
			localStorage.getItem(SESSION_LOCAL_STORAGE_KEY)
		);
		if (!bridge?.data) return;

		const elapsed = Date.now() - bridge.lastActivity;
		if (elapsed > SESSION_INACTIVITY_TTL_MS) {
			localStorage.removeItem(SESSION_LOCAL_STORAGE_KEY);
			return;
		}

		bridge.lastActivity = Date.now();
		localStorage.setItem(SESSION_LOCAL_STORAGE_KEY, JSON.stringify(bridge));
	} catch {
		/* non-critical */
	}
}
